import type {
  Domain,
  DomainWithProjects,
  ModuleMenuItemResponse,
  ModuleResponse,
  Project,
  Workspace,
  WorkspaceSummary,
} from '@protoss/shared';
import * as React from 'react';
import { firebaseAuth } from '../lib/firebase';
import { useAuth } from './AuthContext';

interface WorkspaceContextValue {
  workspace: Workspace | null;
  isLoadingWorkspace: boolean;
  domains: DomainWithProjects[];
  isLoadingDomains: boolean;
  refetchWorkspace: () => Promise<void>;
  refetchDomains: () => Promise<void>;
  /** Available modules for this workspace (ordered by display_order) */
  modules: ModuleResponse[];
  /** Whether the modules fetch has completed */
  isLoadingModules: boolean;
  /** The active module key for the current user. Null until modules are loaded or if no module is assigned. */
  activeModuleKey: string | null;
  /** The full active module definition object. Null until modules are loaded. */
  activeModule: ModuleResponse | null;
  /** Switch to a different module - updates local state and persists preference */
  setActiveModuleKey: (moduleKey: string) => Promise<void>;
  /** Re-fetch modules (e.g. after an admin assigns a module) */
  refetchModules: () => Promise<void>;
  /** DB-driven nav menu items for the active module */
  moduleMenuItems: ModuleMenuItemResponse[];
  /** Whether the module menu fetch has completed */
  isLoadingModuleMenu: boolean;
  /** All workspaces the user belongs to - empty until loaded */
  availableWorkspaces: WorkspaceSummary[];
  /** Switch to a different workspace - persists preference and reloads context */
  switchWorkspace: (workspaceUuid: string) => Promise<void>;
  /**
   * Workspace storage integration (Google Drive) status, resolved once on workspace load so the
   * Drive affordance can render together with the card that uses it (no per-mount fetch / pop-in).
   * `accountEmail` is the connected account (credential.description).
   */
  googleDriveIntegration: GoogleDriveIntegrationStatus;
  /** Re-resolve the Drive status (e.g. after connecting/disconnecting Drive mid-session). */
  refetchGoogleDriveIntegration: () => Promise<void>;
}

interface GoogleDriveIntegrationStatus {
  connected: boolean;
  integrationUuid: string | null;
  accountEmail: string | null;
  isLoading: boolean;
}

const WorkspaceContext = React.createContext<WorkspaceContextValue | null>(null);

/**
 * WorkspaceProvider - provides cached workspace and organization data
 *
 * Fetches workspace and domains/projects data once and shares it across all pages,
 * preventing unnecessary refetches on navigation.
 */
export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user, appUser, isLoading: isAuthLoading } = useAuth();
  const [workspace, setWorkspace] = React.useState<Workspace | null>(null);
  const [isLoadingWorkspace, setIsLoadingWorkspace] = React.useState(true);
  const [domains, setDomains] = React.useState<DomainWithProjects[]>([]);
  const [isLoadingDomains, setIsLoadingDomains] = React.useState(true);
  const [modules, setModules] = React.useState<ModuleResponse[]>([]);
  const [activeModuleKey, setActiveModuleKeyState] = React.useState<string | null>(null);
  const [isLoadingModules, setIsLoadingModules] = React.useState(true);
  const [moduleMenuItems, setModuleMenuItems] = React.useState<ModuleMenuItemResponse[]>([]);
  const [isLoadingModuleMenu, setIsLoadingModuleMenu] = React.useState(true);
  const [availableWorkspaces, setAvailableWorkspaces] = React.useState<WorkspaceSummary[]>([]);
  const [googleDriveIntegration, setGoogleDriveIntegration] =
    React.useState<GoogleDriveIntegrationStatus>({
      connected: false,
      integrationUuid: null,
      accountEmail: null,
      isLoading: true,
    });

  // Resolve the workspace's storage integration (Google Drive) once, up front. Centralizing it
  // here (instead of a per-component fetch) means consumers read an already-resolved status, so a
  // Drive affordance renders together with its card rather than popping in after a late fetch.
  const fetchGoogleDriveIntegration = React.useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      setGoogleDriveIntegration({
        connected: false,
        integrationUuid: null,
        accountEmail: null,
        isLoading: false,
      });
      return;
    }
    try {
      setGoogleDriveIntegration((prev) => ({ ...prev, isLoading: true }));
      const idToken = await currentUser.getIdToken();
      const response = await fetch('/api/integration', {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (response.ok) {
        const { integrations } = (await response.json()) as {
          integrations: {
            uuid: string;
            description: string | null;
            lifecycleStatus: string;
            definition?: { slug?: string };
          }[];
        };
        const drive = integrations.find(
          (i) => i.definition?.slug === 'google-drive' && i.lifecycleStatus === 'active'
        );
        setGoogleDriveIntegration({
          connected: !!drive,
          integrationUuid: drive?.uuid ?? null,
          accountEmail: drive?.description ?? null,
          isLoading: false,
        });
      } else {
        setGoogleDriveIntegration({
          connected: false,
          integrationUuid: null,
          accountEmail: null,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Failed to load Google Drive integration:', error);
      setGoogleDriveIntegration({
        connected: false,
        integrationUuid: null,
        accountEmail: null,
        isLoading: false,
      });
    }
  }, []);

  const fetchWorkspaceList = React.useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) return;

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch('/api/workspace/list', {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (response.ok) {
        const { data } = (await response.json()) as { data: WorkspaceSummary[] };
        setAvailableWorkspaces(data);
      }
    } catch (error) {
      console.error('Failed to load workspace list:', error);
    }
  }, []);

  const fetchWorkspace = React.useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      setIsLoadingWorkspace(false);
      return;
    }

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch('/api/workspace/current', {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (response.ok) {
        const { workspace: data } = (await response.json()) as { workspace: Workspace };
        setWorkspace(data);
      }
    } catch (error) {
      console.error('Failed to load workspace:', error);
    } finally {
      setIsLoadingWorkspace(false);
    }
  }, []);

  const fetchDomains = React.useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      setIsLoadingDomains(false);
      return;
    }

    try {
      setIsLoadingDomains(true);

      const idToken = await currentUser.getIdToken();

      const [domainsResponse, projectsResponse] = await Promise.all([
        fetch('/api/domain', { headers: { Authorization: `Bearer ${idToken}` } }),
        fetch('/api/project', { headers: { Authorization: `Bearer ${idToken}` } }),
      ]);

      if (!domainsResponse.ok) {
        throw new Error(`Failed to fetch domains: ${domainsResponse.statusText}`);
      }

      if (!projectsResponse.ok) {
        throw new Error(`Failed to fetch projects: ${projectsResponse.statusText}`);
      }

      const { domains: domainList } = (await domainsResponse.json()) as { domains: Domain[] };
      const { projects: projectList } = (await projectsResponse.json()) as { projects: Project[] };

      setDomains(
        domainList.map((domain) => ({
          ...domain,
          projects: projectList.filter((p) => p.domainUuid === domain.uuid),
        }))
      );
    } catch (error) {
      console.error('Failed to load domains:', error);
    } finally {
      setIsLoadingDomains(false);
    }
  }, []);

  // Key on uid so this only re-runs on actual sign-in/sign-out, not on
  // re-renders or token refreshes. isAuthLoading guards against running
  // before Firebase has resolved the session on page load.
  const uid = user?.uid;
  // appUser is set only AFTER the POST /api/auth/firebase exchange resolves (which
  // creates the server-side user and accepts any pending invite -> workspace membership
  // + module roles). Gate workspace-data fetches on it so they never race the exchange.
  const hasAppUser = appUser != null;

  const fetchModules = React.useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      setIsLoadingModules(false);
      return;
    }

    try {
      setIsLoadingModules(true);
      const idToken = await currentUser.getIdToken();
      const response = await fetch('/api/workspace/modules', {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (response.ok) {
        const data = (await response.json()) as {
          modules: ModuleResponse[];
          activeModuleKey: string | null;
        };
        setModules(data.modules);
        setActiveModuleKeyState(data.activeModuleKey);
      }
    } catch (error) {
      console.error('Failed to load modules:', error);
    } finally {
      setIsLoadingModules(false);
    }
  }, []);

  const fetchModuleMenu = React.useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      setIsLoadingModuleMenu(false);
      return;
    }
    try {
      setIsLoadingModuleMenu(true);
      const idToken = await currentUser.getIdToken();
      const response = await fetch('/api/workspace/module-menu', {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (response.ok) {
        const data = (await response.json()) as { items: ModuleMenuItemResponse[] };
        setModuleMenuItems(data.items);
      }
    } catch (error) {
      console.error('Failed to load module menu:', error);
    } finally {
      setIsLoadingModuleMenu(false);
    }
  }, []);

  const setActiveModuleKey = React.useCallback(
    async (moduleKey: string) => {
      setActiveModuleKeyState(moduleKey);

      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) return;

      try {
        const idToken = await currentUser.getIdToken();
        await fetch('/api/workspace/module-preference', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ moduleKey }),
        });
        // Preference is now committed - re-fetch the module menu so nav items
        // reflect the new active module.
        await fetchModuleMenu();
      } catch (error) {
        console.error('Failed to persist module preference:', error);
      }
    },
    [fetchModuleMenu]
  );

  // refetchModules re-fetches both the modules list AND the module menu, so the
  // nav items are always in sync after an admin assigns or changes a module.
  const refetchModules = React.useCallback(async () => {
    await fetchModules();
    await fetchModuleMenu();
  }, [fetchModules, fetchModuleMenu]);

  React.useEffect(() => {
    if (isAuthLoading) return;

    if (!uid) {
      setWorkspace(null);
      setDomains([]);
      setModules([]);
      setActiveModuleKeyState(null);
      setModuleMenuItems([]);
      setAvailableWorkspaces([]);
      setGoogleDriveIntegration({
        connected: false,
        integrationUuid: null,
        accountEmail: null,
        isLoading: false,
      });
      setIsLoadingWorkspace(false);
      setIsLoadingDomains(false);
      setIsLoadingModules(false);
      setIsLoadingModuleMenu(false);
      return;
    }

    // Logged in, but the POST /api/auth/firebase exchange that creates the server-side
    // user (and accepts pending invites -> workspace membership + module roles) may not
    // have completed yet. Fetching now would race it: /api/workspace/modules returns an
    // empty/401 result, leaving modules=[] with isLoadingModules=false -> NoModulePage,
    // which only clears on a manual refresh. Wait for appUser. The initial loading flags
    // stay true so the guard shows a spinner, never NoModulePage, in this window.
    if (!hasAppUser) return;

    fetchWorkspace();
    fetchDomains();
    fetchModules();
    fetchModuleMenu();
    fetchWorkspaceList();
    fetchGoogleDriveIntegration();
  }, [
    uid,
    hasAppUser,
    isAuthLoading,
    fetchWorkspace,
    fetchDomains,
    fetchModules,
    fetchModuleMenu,
    fetchWorkspaceList,
    fetchGoogleDriveIntegration,
  ]);

  // After sign-in, align the backend's lastSelectedAt to match the workspace resolved from
  // the ?workspace=<slug> URL param. Only runs in DEV - in UAT/PROD every route is already
  // constrained by the hostname (resolveWorkspaceFromHostPlugin), so lastSelectedAt is irrelevant.
  const urlWorkspaceAppliedForUid = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!uid) {
      urlWorkspaceAppliedForUid.current = null;
      return;
    }
    // In UAT/PROD all API routes use hostname-based workspace resolution - skip entirely.
    if (!import.meta.env.DEV) return;
    if (availableWorkspaces.length === 0) return; // wait for list to load
    if (urlWorkspaceAppliedForUid.current === uid) return; // already done for this session

    // DEV only: use ?workspace=<slug> URL param
    const slug = new URLSearchParams(window.location.search).get('workspace');
    urlWorkspaceAppliedForUid.current = uid; // mark before async to prevent double-run

    if (!slug) return;
    const match = availableWorkspaces.find((w) => w.slug === slug);
    if (!match) return;

    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) return;

    void (async () => {
      try {
        const idToken = await currentUser.getIdToken();
        await fetch('/api/workspace/select', {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspaceUuid: match.uuid }),
        });
        // Re-fetch all workspace-scoped data now that lastSelectedAt is aligned.
        // Other routes (domain, project, module) use lastSelectedAt-based membership,
        // so they need to be refreshed after the select to show the correct workspace's data.
        void Promise.all([
          fetchWorkspace(),
          fetchDomains(),
          fetchModules(),
          fetchModuleMenu(),
          fetchGoogleDriveIntegration(),
        ]);
      } catch {
        // Non-fatal - workspace will stay as whatever the backend last recorded
      }
    })();
  }, [
    uid,
    availableWorkspaces,
    fetchWorkspace,
    fetchDomains,
    fetchModules,
    fetchModuleMenu,
    fetchGoogleDriveIntegration,
  ]);

  // Re-fetch module menu whenever the active module changes (menu items are module-scoped).
  // Skip on initial null→key transition that is already covered by the uid effect above.
  const prevActiveModuleKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (activeModuleKey === null) return;
    if (prevActiveModuleKeyRef.current === null) {
      // First assignment - already fetched in uid effect, just record it.
      prevActiveModuleKeyRef.current = activeModuleKey;
      return;
    }
    if (prevActiveModuleKeyRef.current === activeModuleKey) return;
    prevActiveModuleKeyRef.current = activeModuleKey;
    void fetchModuleMenu();
  }, [activeModuleKey, fetchModuleMenu]);

  // Drive can be connected/disconnected mid-session on the Integrations page (OAuth popup), which
  // broadcasts on the 'google-drive-oauth' channel. Re-resolve the cached status on any such
  // message so the Drive affordance stays accurate without a full reload. Defensive: a no-op if
  // the browser lacks BroadcastChannel.
  React.useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('google-drive-oauth');
    channel.onmessage = () => void fetchGoogleDriveIntegration();
    return () => channel.close();
  }, [fetchGoogleDriveIntegration]);

  const switchWorkspace = React.useCallback(
    async (workspaceUuid: string) => {
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) return;

      try {
        const idToken = await currentUser.getIdToken();
        await fetch('/api/workspace/select', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ workspaceUuid }),
        });
      } catch (error) {
        console.error('Failed to switch workspace:', error);
      }

      // In dev mode, navigate with ?workspace=<slug> so main.tsx bootstraps the
      // correct Firebase config and the sign-in page dev picker stays in sync.
      if (import.meta.env.DEV) {
        const slug = availableWorkspaces.find((w) => w.uuid === workspaceUuid)?.slug;
        if (slug) {
          window.location.assign(`/?workspace=${encodeURIComponent(slug)}`);
          return;
        }
      }
      // In multi-tenant deployments each workspace has its own subdomain. Navigate
      // to the target workspace's custom domain so the hostname-based resolver picks
      // the correct workspace - a plain reload would stay on the current subdomain.
      const target = availableWorkspaces.find((w) => w.uuid === workspaceUuid);
      if (target?.customDomain) {
        window.location.assign(`https://${target.customDomain}/`);
        return;
      }
      // Reload the page so all context is re-initialized for the new workspace.
      window.location.reload();
    },
    [availableWorkspaces]
  );

  const value = React.useMemo(
    () => ({
      workspace,
      isLoadingWorkspace,
      domains,
      isLoadingDomains,
      refetchWorkspace: fetchWorkspace,
      refetchDomains: fetchDomains,
      modules,
      isLoadingModules,
      activeModuleKey,
      activeModule: modules.find((m) => m.key === activeModuleKey) ?? null,
      setActiveModuleKey,
      refetchModules: refetchModules,
      moduleMenuItems,
      isLoadingModuleMenu,
      availableWorkspaces,
      switchWorkspace,
      googleDriveIntegration,
      refetchGoogleDriveIntegration: fetchGoogleDriveIntegration,
    }),
    [
      workspace,
      isLoadingWorkspace,
      domains,
      isLoadingDomains,
      fetchWorkspace,
      fetchDomains,
      modules,
      isLoadingModules,
      activeModuleKey,
      setActiveModuleKey,
      refetchModules,
      moduleMenuItems,
      isLoadingModuleMenu,
      availableWorkspaces,
      switchWorkspace,
      googleDriveIntegration,
      fetchGoogleDriveIntegration,
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/**
 * useWorkspace - access workspace and organization data
 *
 * Returns cached workspace and domains data shared across all pages.
 */
export function useWorkspace() {
  const context = React.useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
}
